/* eslint-disable no-console */
import { PipelineSeriesByRegionColour, PipelineSeriesOnBuild } from '@nova/types';

import { ClientTextureUtils } from './cl-texture-utils';
import { IScaleParams } from './models/scale-params.interface';
import { IScalePipelineResult } from './models/scale-pipeline-result.interface';
import { StaticPipelineOperations } from './StaticPipelineOps';
import { TextureManipulationDrawSeries } from './texture-manip-drawSeries';
import { TextureManipulationSeriesRegionMasking } from './texture-manip-drawSeriesByRegion';

// TODO: We can optimize this chain by having pipeline classes take Uint8Array, not necessarily ImageData
// They would still need to have accompanying width and height values
// And should still always accept ImageData for when they are used independently, not apart of the specific chain.

export class ClientTextureGeneration {
  public static CreateDirtLayeredTexture(img: HTMLImageElement, dirtTileImgData: ImageData, gl?: WebGLRenderingContext): HTMLImageElement {
    const partImgData = ClientTextureUtils.GetImageDataFromImage(img);
    const tiledDirtPixelData: Uint8Array = StaticPipelineOperations.GetTiled(dirtTileImgData, partImgData.width, partImgData.height, gl);
    const tiledDirtImageData: ImageData = ClientTextureUtils.GetImageDataFromPixels(tiledDirtPixelData, partImgData.width, partImgData.height);
    const tiledDirtMaskedPixelData: Uint8Array = StaticPipelineOperations.GetMasked(tiledDirtImageData, partImgData, false, gl);
    const tiledDirtMaskedImageData: ImageData = ClientTextureUtils.GetImageDataFromPixels(tiledDirtMaskedPixelData, partImgData.width, partImgData.height);
    const dirtyPartPixelData: Uint8Array = StaticPipelineOperations.GetOverlayed(partImgData, tiledDirtMaskedImageData, gl);
    const dirtyPartImageData: ImageData = ClientTextureUtils.GetImageDataFromPixels(dirtyPartPixelData, partImgData.width, partImgData.height);
    return ClientTextureUtils.GetImageFromImageData(dirtyPartImageData);
  }

  public static CreateTiled(tileImgData: ImageData, width: number, height: number, gl?: WebGLRenderingContext): HTMLImageElement {
    const tiledPixelData: Uint8Array = StaticPipelineOperations.GetTiled(tileImgData, width, height, gl);
    const tiledImageData: ImageData = ClientTextureUtils.GetImageDataFromPixels(tiledPixelData, width, height);
    return ClientTextureUtils.GetImageFromImageData(tiledImageData);
  }

  /**
   * If the imageToMask and imageAsMask are not the same dimensions, the 'imageAsMask' will be scaled to fit the 'imageToMask'.
   * If such scaling is required, be sure that the css width/height of the 'imageAsMask' is not modified,
   * or at least matches the intrinsic texture size, or the canvas created will be set to the set styling,
   * while the intrinsic size/pixel data will be used for processing.
   * @param imageToMask
   * @param imageAsMask
   * @returns
   */
  public static CreateMasked(imageToMask: HTMLImageElement, imageAsMask: HTMLImageElement, gl?: WebGLRenderingContext): HTMLImageElement {
    const imageToMaskData = ClientTextureUtils.GetImageDataFromImage(imageToMask);
    let imageAsMaskData = ClientTextureUtils.GetImageDataFromImage(imageAsMask);

    if (imageToMaskData.width !== imageAsMaskData.width || imageToMaskData.height !== imageAsMaskData.height) {
      console.warn(`ClientTextureGeneration::CreateMasked: Image dimensions do not match.` +
        `A new 'imageAsMask' mask texture will be created and scaled-to-fit 'imageToMask'.`);

      const scaleResult: IScalePipelineResult =
        StaticPipelineOperations.GetScaled(imageAsMaskData, { widthToPx: imageToMaskData.width, heightToPx: imageToMaskData.height }, gl);
      imageAsMaskData = ClientTextureUtils.GetImageDataFromPixels(scaleResult.pixelData, scaleResult.newWidthPx, scaleResult.newHeightPx);
    }

    const maskedPixelData: Uint8Array = StaticPipelineOperations.GetMasked(imageToMaskData, imageAsMaskData, false, gl);
    const maskedImageData: ImageData = ClientTextureUtils.GetImageDataFromPixels(maskedPixelData, imageToMaskData.width, imageToMaskData.height);
    return ClientTextureUtils.GetImageFromImageData(maskedImageData);
  }

  public static CreateTinted(img: HTMLImageElement, colour: number[], gl?: WebGLRenderingContext): HTMLImageElement {
    const imgData = ClientTextureUtils.GetImageDataFromImage(img);
    const tintedPixelData: Uint8Array = StaticPipelineOperations.GetColourTinted(imgData, colour, gl);
    const tintedImageData: ImageData = ClientTextureUtils.GetImageDataFromPixels(tintedPixelData, imgData.width, imgData.height);
    return ClientTextureUtils.GetImageFromImageData(tintedImageData);
  }

  public static CreateScaled(img: HTMLImageElement, scale: IScaleParams, gl?: WebGLRenderingContext): HTMLImageElement {
    const imgData = ClientTextureUtils.GetImageDataFromImage(img);
    const scaleResult: IScalePipelineResult = StaticPipelineOperations.GetScaled(imgData, scale, gl);
    const scaledImageData: ImageData = ClientTextureUtils.GetImageDataFromPixels(scaleResult.pixelData, scaleResult.newWidthPx, scaleResult.newHeightPx);
    return ClientTextureUtils.GetImageFromImageData(scaledImageData);
  }

  public static CreateFromPipelineSeries(img: HTMLImageElement, seriesBuilt: PipelineSeriesOnBuild, gl?: WebGLRenderingContext): HTMLImageElement {
    if (seriesBuilt.length === 0) { return img; }

    // We'll just create and use this functionality once.
    // TODO: Establish any destructors required in the manipulation objects
    const texManipSeries: TextureManipulationDrawSeries = new TextureManipulationDrawSeries();

    const imgData = ClientTextureUtils.GetImageDataFromImage(img);

    // ! Image data width and height are fine for now, so long as none of the series options are allowing for effects beyond the existing image bounds.
    const glToUse = gl || ClientTextureGeneration.CreateCanvasCtx(imgData.width, imgData.height);

    texManipSeries.InitPipelines(glToUse, imgData.width, imgData.height);
    texManipSeries.CreateBaseTexture(imgData);

    // * Essentially Just calls "RunPipelineSeries" below.
    const lastType = texManipSeries.Draw(seriesBuilt);
    const finalPixelData: Uint8Array | null = texManipSeries.GetPixelData(lastType);
    const finalImageData: ImageData | null = ClientTextureUtils.GetImageDataFromPixels(finalPixelData, imgData.width, imgData.height);
    texManipSeries.Destroy();

    return ClientTextureUtils.GetImageFromImageData(finalImageData);
  }

  public static CreateFromPipelineSeriesByRegionColour(
    baseImg: HTMLImageElement, mask: HTMLImageElement,
    seriesByRegionColour: PipelineSeriesByRegionColour,
    gl?: WebGLRenderingContext
  ): HTMLImageElement {
    if (Object.keys(seriesByRegionColour).length === 0) { return mask; }

    // We'll just create and use this functionality once.
    // TODO: Establish any destructors required in the manipulation objects
    const texManipSeriesMasked: TextureManipulationSeriesRegionMasking = new TextureManipulationSeriesRegionMasking();

    const baseImgData = ClientTextureUtils.GetImageDataFromImage(baseImg);
    const maskImgData = ClientTextureUtils.GetImageDataFromImage(mask);

    const glToUse = gl || ClientTextureGeneration.CreateCanvasCtx(baseImgData.width, baseImgData.height);

    texManipSeriesMasked.InitPipelines(glToUse, baseImgData.width, baseImgData.height);
    texManipSeriesMasked.CreateBlankOutputTexture(baseImgData.width, baseImgData.height);
    texManipSeriesMasked.CreateBaseTexture(baseImgData);
    texManipSeriesMasked.CreateMaskTexture(maskImgData);

    const finalPixelData: Uint8Array = texManipSeriesMasked.DrawAndGetPixelData(seriesByRegionColour);
    const finalImageData: ImageData = ClientTextureUtils.GetImageDataFromPixels(finalPixelData, baseImgData.width, baseImgData.height);

    texManipSeriesMasked.Destroy();

    return ClientTextureUtils.GetImageFromImageData(finalImageData);
  }

  private static CreateCanvasCtx(resizeWidth: number, resizeHeight: number): WebGLRenderingContext {
    const { gl: glInternal } = ClientTextureUtils.CreateCanvasWebGL(resizeWidth, resizeHeight);
    return glInternal;
  }

  public static Cleanup() {
    ClientTextureUtils.DestroyCanvas();
  }
}
